import { Card, CardContent, CardHeader } from '@mui/material';
import { BoardParams, BoardType, ParamsPanelProps } from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import ChevronParamsPanel from 'boardClasses/chevron/ChevronParamsPanel';
import PinwheelParamsPanel from 'boardClasses/pinwheel/PinwheelParamsPanel';
import PlaidParamsPanel from 'boardClasses/plaid/PlaidParamsPanel';
import QuarterSquareParamsPanel from 'boardClasses/quarterSquare/QuarterSquareParamsPanel';
import StripsParamsPanel from 'boardClasses/strips/StripsParamsPanel';
import StripsAdvancedParamsPanel from 'boardClasses/stripsAdvanced/StripsAdvancedParamsPanel';
import WeaveParamsPanel from 'boardClasses/weave/WeaveParamsPanel';
import ZigZagParamsPanel from 'boardClasses/zigZag/ZigZagParamsPanel';
import { useUpdateBoard } from 'state/recoilBoardActions';

const PARAMS_PANELS_BY_TYPE: Record<
  BoardType,
  (props: ParamsPanelProps<BoardType>) => JSX.Element | null
> = {
  [BoardType.Chevron]: ChevronParamsPanel,
  [BoardType.Pinwheel]: PinwheelParamsPanel,
  [BoardType.Plaid]: PlaidParamsPanel,
  [BoardType.QuarterSquare]: QuarterSquareParamsPanel,
  [BoardType.Strips]: StripsParamsPanel,
  [BoardType.StripsAdvanced]: StripsAdvancedParamsPanel,
  [BoardType.Weave]: WeaveParamsPanel,
  [BoardType.ZigZag]: ZigZagParamsPanel,
};

function getParamsPanel<T extends BoardType>(boardType: BoardType) {
  return PARAMS_PANELS_BY_TYPE[boardType] as (
    props: ParamsPanelProps<T>
  ) => JSX.Element | null;
}

function EditorParamsCard<T extends BoardType>(props: {
  boardInstance: BoardBase<T>;
  onSelectionChange: ParamsPanelProps<T>['onSelectionChange'];
  selection: ParamsPanelProps<T>['selection'];
}) {
  const { boardInstance, onSelectionChange, selection } = props;

  const updateBoard = useUpdateBoard<T>();

  const onParamChange =
    <K extends keyof BoardParams<T>, V extends BoardParams<T>[K]>(param: K) =>
    (value: V) => {
      const updatedBoardParams = {
        ...boardInstance.params,
        [param]: value,
      };
      updateBoard(boardInstance.updateParams(updatedBoardParams));
    };

  const ParamsPanel = getParamsPanel<T>(boardInstance.type);

  return (
    <Card>
      <CardHeader title="Parameters" subheader={boardInstance.type} />
      <CardContent>
        <form
          noValidate
          autoComplete="off"
          onSubmit={(e) => e.preventDefault()}
        >
          <ParamsPanel
            onParamChange={onParamChange}
            onSelectionChange={onSelectionChange}
            selection={selection}
            params={boardInstance.params}
            units={boardInstance.units}
          />
        </form>
      </CardContent>
    </Card>
  );
}

export default EditorParamsCard;
